import React, { Component } from 'react';
import './App.css';
import './responsive.css';
import 'typeface-roboto';
import CssBaseline from '@mui/material/CssBaseline';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import NavContainer from './containers/NavContainer';
import Footer from './containers/FooterContainer';
import { Link, Route, Routes } from 'react-router-dom';
import APIContainer from './containers/APIContainer';
import EmptySearchContainer from './containers/EmptySearchContainer';
import LandingPageContainer from './containers/LandingPageContainer';
import NavMenu from './components/NavMenu';
import ProfilePage from './containers/ProfilePage';
import SignUp from './components/SignUp';
import { Element } from 'react-scroll';
import AboutContent from './content/AboutContent';
import ContactContent from './content/ContactContent';
import SellMyHomeContent from './content/SellMyHomeContent';
import LandingPageContent from './content/LandingPageContent';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import PropertyService from './services/api';
import AdminUsageDashboard from './containers/AdminUsageDashboard';
import EstimateLoading from './components/EstimateLoading';
import PageVisitTracker from './components/PageVisitTracker';
import { buildSourceStatuses } from './utils/estimateSources';

const QUOTA_LIMIT_MESSAGE = 'You’ve reached the maximum number of free searches. If you would like more searches, please contact us and we will provide you with more.';

const getInitialEstimates = () => ({
  zillowEstimate: {
    id: 1,
    site_name: 'Zillow',
    img: './img/zillow-fit.png',
    link: '',
    value: null,
    active: true
  },
  realtorEstimate: {
    id: 2,
    site_name: 'Realtor',
    img: './img/realtor-fit.png',
    listing_id: '',
    link: '',
    value: null,
    active: true
  },
  redfinEstimate: {
    id: 3,
    site_name: 'Redfin',
    img: './img/redfin-fit.png',
    listing_id: '',
    link: '',
    value: null,
    active: true
  },
  melissaEstimate: {
    id: 4,
    site_name: 'Melissa',
    img: './img/melissa-fit.png',
    link: '',
    value: null,
    active: false,
    visible: false
  },
  mashvisorEstimate: {
    id: 5,
    site_name: 'Mashvisor',
    img: './img/mash-fit.png',
    value: null,
    active: false,
    visible: false
  },
  homesEstimate: {
    id: 6,
    site_name: 'Homes.com',
    img: './img/homes-fit.png',
    link: '',
    value: null,
    active: true
  },
  dataTreeEstimate: {
    id: 7,
    site_name: 'Data Tree',
    img: './img/datatree-fit.png',
    value: null,
    active: false,
    visible: false
  },
  estatedEstimate: {
    id: 8,
    site_name: 'Estated',
    img: './img/estated-fit.png',
    value: null,
    active: false,
    visible: false
  },
  attomEstimate: {
    id: 9,
    site_name: 'ATTOM',
    img: './img/attom-fit.svg',
    link: '',
    value: null,
    active: true
  },
  rentcastEstimate: {
    id: 10,
    site_name: 'RentCast',
    img: './img/rentcast-fit.svg',
    link: '',
    value: null,
    active: true
  }
});

export default class App extends Component {
  state = {
    allHomes: [],
    foundHome: {},
    estimates: getInitialEstimates(),
    isLoggedIn: false,
    user: {},
    isLoading: false,
    extraHomeData: {},
    loading: false,
    error: null,
    searchPerformed: false,
    searchResetToken: 0,
    sourceStatuses: [],
    quotaModalDismissed: false
  };

  resetSearch = () => {
    this.setState(prevState => ({
      foundHome: {},
      estimates: getInitialEstimates(),
      extraHomeData: {},
      loading: false,
      error: null,
      quotaModalDismissed: false,
      searchPerformed: false,
      searchResetToken: prevState.searchResetToken + 1,
      sourceStatuses: []
    }));
  };

  handleSearch = async (searchData) => {
    this.setState({ loading: true, sourceStatuses: [], quotaModalDismissed: false });
    try {
      const [street, city, state, zip] = this.parseAddress(searchData.address);
      const estimates = await PropertyService.getEstimates(street, city, state, zip);
      const homeFacts = estimates.home || {};
      
      this.setState(prevState => ({
        estimates: {
          ...prevState.estimates,
          zillowEstimate: {
            ...prevState.estimates.zillowEstimate,
            value: estimates.zillow?.zestimate || null,
            link: estimates.zillow?.homeDetails || ''
          },
          realtorEstimate: {
            ...prevState.estimates.realtorEstimate,
            value: estimates.realtor?.value || null,
            link: estimates.realtor?.link || ''
          },
          redfinEstimate: {
            ...prevState.estimates.redfinEstimate,
            value: estimates.redfin?.value || null,
            link: estimates.redfin?.link || ''
          },
          melissaEstimate: {
            ...prevState.estimates.melissaEstimate,
            value: estimates.melissa?.value || estimates.melissa?.Records?.[0]?.CurrentDeed?.SalePrice || null
          },
          mashvisorEstimate: {
            ...prevState.estimates.mashvisorEstimate,
            value: estimates.mashvisor?.value || null
          },
          homesEstimate: {
            ...prevState.estimates.homesEstimate,
            value: estimates.homes?.value || estimates.realtyMole?.price || null,
            link: estimates.homes?.link || estimates.realtyMole?.listingUrl || ''
          },
          attomEstimate: {
            ...prevState.estimates.attomEstimate,
            value: estimates.attom?.value || null,
            link: estimates.attom?.link || ''
          },
          rentcastEstimate: {
            ...prevState.estimates.rentcastEstimate,
            value: estimates.rentcast?.value || null,
            link: estimates.rentcast?.link || ''
          }
        },
        foundHome: {
          street_address: street,
          city: city,
          state: state,
          zip_code: zip,
          home_type: homeFacts.home_type || '',
          bedrooms: homeFacts.bedrooms || estimates.melissa?.Records?.[0]?.BuildingInfo?.TotalBedrooms || '3',
          bathrooms: homeFacts.bathrooms || estimates.melissa?.Records?.[0]?.BuildingInfo?.TotalBathrooms || '2',
          sqft: homeFacts.sqft || estimates.melissa?.Records?.[0]?.BuildingInfo?.TotalSquareFeet || '2,000',
          lot_size: homeFacts.lot_size || '',
          year_built: homeFacts.year_built || estimates.melissa?.Records?.[0]?.BuildingInfo?.YearBuilt || '2000',
          sold_price: homeFacts.sold_price || estimates.melissa?.Records?.[0]?.CurrentDeed?.SalePrice || '',
          sold_date: homeFacts.sold_date || estimates.melissa?.Records?.[0]?.CurrentDeed?.SaleDate || '',
          lat: homeFacts.lat || searchData.lat,
          long: homeFacts.long || searchData.long
        },
        loading: false,
        searchPerformed: true,
        sourceStatuses: buildSourceStatuses(estimates),
        error: null
      }));
    } catch (error) {
      console.error('Error fetching estimates:', error);
      this.setState({ 
        loading: false,
        error: error.status === 429 ? error.message : 'Failed to fetch property estimates. Please try again.'
      });
    }
  }

  parseAddress = (address) => {
    // Simple address parser - you might want to use a more robust solution
    const parts = address.split(',').map(part => part.trim());
    const street = parts[0];
    const city = parts[1];
    const stateZip = parts[2].split(' ');
    const state = stateZip[0];
    const zip = stateZip[1];
    return [street, city, state, zip];
  }

  nodeFinder = (data) => {
    if (data !== null || data !== undefined) {
      return data;
    } else {
      return 'N/A';
    }
  };

  parseHome = (homeData) => {
    const homeObj ={
      home_type: this.nodeFinder(homeData.useCode),
      year_built: this.nodeFinder(homeData.yearBuilt),
      sqft: this.nodeFinder(homeData.finishedSqFt),
      lot_size: this.nodeFinder(homeData.lotSizeSqFt),
      bedrooms: this.nodeFinder(homeData.bedrooms),
      bathrooms: this.nodeFinder(homeData.bathrooms),
      total_rooms: this.nodeFinder(homeData.totalRooms),
      sold_date: this.nodeFinder(homeData.lastSoldDate),
      street_address: this.nodeFinder(homeData.address[0].street[0]),
      city: this.nodeFinder(homeData.address[0].city[0]),
      state: this.nodeFinder(homeData.address[0].state[0]),
      zip_code: this.nodeFinder(homeData.address[0].zipcode[0]),
      lat: this.nodeFinder(homeData.address[0].latitude),
      long: this.nodeFinder(homeData.address[0].longitude),
      link_to: this.nodeFinder(homeData.links[0].homedetails[0]),
    }
    return homeObj;
  }
  
  parseZillowEstimate = homeData => {
    const zData = {
      value: Number(homeData.zestimate[0].amount[0]._),
      link: homeData.links[0].homedetails[0]
    }
    return zData
  }

  errorCatch = (data) => {
    if(data.querySelector('code').innerHTML !== '0') {
      return true
    } else {
      return false
    }
  }

  deleteEstimate = (id) => {
    for(let i = 0; i < this.state.estimates; i++) {
      console.log(this.state.estimates[i])
    }
  }
    

  savePage = () => {
    const address = this.state.foundHome.street_address
    const divToDisplay = document.querySelector('#print-area')
    html2canvas(divToDisplay, {
      allowTaint: false,
      useCORS: true,
    })
    .then(function(canvas) {
      const divImage = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "pt", "legal");
      // const imgProps = pdf.getImageProperties(divImage);
      // const pdfWidth = pdf.internal.pageSize.getWidth();
      // const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(divImage, 'PNG', 0, 0, 600, 1080);
      pdf.save(`${address}.pdf`);
    })
  }

  getSearchResults = queryObj => {
    this.handleSearch(queryObj);
  };

  dismissQuotaModal = () => {
    this.setState({ quotaModalDismissed: true });
  };

  hasReachedQuotaLimit = () => {
    return Boolean(this.state.error?.includes('maximum number'));
  };

  handleChange = e => {
    console.log(e.target.value);
  };

  isEmpty = obj => {
    return !obj || Object.keys(obj).length === 0;
  };

  renderEstimateTool = () => {
    const hasResults = !this.isEmpty(this.state.foundHome);
    const showQuotaModal = this.hasReachedQuotaLimit() && !this.state.quotaModalDismissed;

    return (
      <>
        <NavContainer
          loggedin={this.state.isLoggedIn}
          search={this.getSearchResults}
          onResetSearch={this.resetSearch}
          searchResetToken={this.state.searchResetToken}
          hasResults={hasResults}
        />
        {this.state.error && !this.hasReachedQuotaLimit() ? <div className="lookup-error">{this.state.error}</div> : null}
        <Dialog open={showQuotaModal} onClose={this.dismissQuotaModal} aria-labelledby="quota-limit-title">
          <DialogTitle id="quota-limit-title">
            Free search limit reached
            <IconButton aria-label="Close quota message" onClick={this.dismissQuotaModal} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <p>{QUOTA_LIMIT_MESSAGE}</p>
          </DialogContent>
          <DialogActions>
            <Button component={Link} to="/contact?request=more-searches" variant="contained" onClick={this.dismissQuotaModal}>
              Contact Us Now
            </Button>
          </DialogActions>
        </Dialog>
        <div className={`flex-wrapper ${hasResults ? '' : 'empty-search-wrapper'}`}>
          <Element name="search-results">
            {hasResults ? (
              <APIContainer
                home={this.state.foundHome}
                extraHomeData={this.state.extraHomeData}
                estimates={this.state.estimates}
                sourceStatuses={this.state.sourceStatuses}
                savePage={this.savePage}
                onResetSearch={this.resetSearch}
              />
            ) : (
              <EmptySearchContainer isLoading={this.state.isLoading}/>
            )}
          </Element>
          {hasResults && <Footer />}
        </div>
      </>
    );
  };

  render() {
    return (
      <div className="App">
        {this.state.isLoading || this.state.loading ?
          <EstimateLoading /> : null}
        <CssBaseline />
        <PageVisitTracker />
        <Routes>
          <Route exact path="/admin" element={<AdminUsageDashboard />} />
          <Route exact path="/profile" element={<ProfilePage />} />
          <Route exact path="/signup" element={<SignUp />} />
          <Route exact path="/about" element={
            <>
              <div className="header">
                <NavMenu onResetSearch={this.resetSearch} />
              </div>
              <div className="flex-wrapper">
                <AboutContent />
                <Footer />
              </div>
            </>
          } />
          <Route exact path="/contact" element={
            <>
              <div className="nav nav-empty-search contact-page-hero">
                <div className="fade contact-page-content">
                  <NavMenu onResetSearch={this.resetSearch} />
                  <ContactContent />
                </div>
              </div>
              <Footer />
            </>
          } />
          <Route exact path="/sell-my-home" element={
            <>
              <div className="header">
                <NavMenu onResetSearch={this.resetSearch} />
              </div>
              <div className="flex-wrapper">
                <SellMyHomeContent />
                <Footer />
              </div>
            </>
          } />
          <Route exact path="/learn-more" element={
            <>
              <LandingPageContainer
                search={this.getSearchResults}
                onResetSearch={this.resetSearch}
              />
              <LandingPageContent />
              <Footer />
            </>
          } />
          <Route exact path="/estimates" element={this.renderEstimateTool()} />
          <Route path="/" element={
            this.renderEstimateTool()
          } />
        </Routes>
      </div>
    );
  }
};
